/**
 * 雪花算法ID生成服务
 * 解决问题：
 * 1. 机器ID硬编码问题（支持分布式环境动态配置）
 * 2. 时钟回拨问题（自动等待至时间恢复正常）
 * 3. 类型不严谨问题（移除any类型，明确类型定义）
 * 4. 序列号溢出问题（自动等待至下一毫秒）
 */
export class SnowflakeService {
  // 雪花算法结构常量（总位数：64）
  private static readonly MID_BITS = 5; // 机器ID位数（支持0-31共32台机器）
  private static readonly SEQUENCE_BITS = 12; // 序列号位数（同一毫秒内最多4096个ID）

  // 最大值计算（位运算边界）
  private static readonly MAX_MID = (1 << SnowflakeService.MID_BITS) - 1; // 31
  private static readonly MAX_SEQUENCE = (1 << SnowflakeService.SEQUENCE_BITS) - 1; // 4095

  // 实例属性
  private readonly epoch: number; // 纪元开始时间（毫秒）
  private readonly mid: number; // 机器ID（0-31）
  private lastTimestamp: number; // 上次生成ID的时间戳
  private sequence: number; // 当前毫秒内的序列号


  constructor() {
    // 1. 初始化纪元时间（2021-01-01 00:00:00，可根据业务调整）
    this.epoch = 1609459200000;

    // 2. 从环境变量获取机器ID（分布式部署时通过配置区分实例）
    this.mid = this.getMachineIdFromEnv();

    // 3. 初始化状态变量
    this.lastTimestamp = -1;
    this.sequence = 0;
  }


  /**
   * 生成唯一雪花ID
   * @returns 雪花ID字符串
   */
  public generate(): string {
    let currentTimestamp = this.getCurrentTimestamp();

    // 处理时钟回拨（当前时间 < 上次时间，说明时钟倒退）
    if (currentTimestamp < this.lastTimestamp) {
      const offset = this.lastTimestamp - currentTimestamp;
      // 等待至时间恢复正常（最多等待1秒，避免无限阻塞）
      if (offset > 1000) {
        throw new Error(`时钟回拨异常，偏移量${offset}ms超过阈值`);
      }
      // 循环等待，直到时间 >= 上次时间戳
      while (currentTimestamp < this.lastTimestamp) {
        currentTimestamp = this.getCurrentTimestamp();
      }
    }

    // 同一毫秒内：序列号自增
    if (currentTimestamp === this.lastTimestamp) {
      this.sequence += 1;
      // 序列号溢出（超过4095）：等待至下一毫秒
      if (this.sequence > SnowflakeService.MAX_SEQUENCE) {
        currentTimestamp = this.waitNextMillisecond(currentTimestamp);
        this.sequence = 0;
      }
    } else {
      // 新的毫秒：重置序列号
      this.sequence = 0;
    }

    // 更新上次时间戳
    this.lastTimestamp = currentTimestamp;

    // 拼接雪花ID（64位）：时间戳差 + 机器ID + 序列号
    const timestampDiff = currentTimestamp - this.epoch;
    const snowflakeId = (
      (BigInt(timestampDiff) << BigInt(SnowflakeService.MID_BITS + SnowflakeService.SEQUENCE_BITS)) |
      (BigInt(this.mid) << BigInt(SnowflakeService.SEQUENCE_BITS)) |
      BigInt(this.sequence)
    ).toString();

    return snowflakeId;
  }


  /**
   * 从环境变量获取机器ID并校验
   * 分布式部署时，通过环境变量 SNOWFLAKE_MID 为每个实例配置唯一ID（0-31）
   */
  private getMachineIdFromEnv(): number {
    const midStr = '1' // process.env.SNOWFLAKE_MID || '1'; // 默认1（单实例场景）
    const mid = Number(midStr);

    if (isNaN(mid) || mid < 0 || mid > SnowflakeService.MAX_MID) {
      throw new Error(`机器ID必须是0-${SnowflakeService.MAX_MID}之间的整数`);
    }

    return mid;
  }


  /**
   * 获取当前时间戳（毫秒）
   */
  private getCurrentTimestamp(): number {
    return Date.now();
  }


  /**
   * 等待至下一毫秒（确保时间戳递增）
   */
  private waitNextMillisecond(currentTimestamp: number): number {
    let nextTimestamp = this.getCurrentTimestamp();
    while (nextTimestamp <= currentTimestamp) {
      nextTimestamp = this.getCurrentTimestamp();
    }
    return nextTimestamp;
  }
}
